import AssignedAppsPanel from './AssignedAppsPanel';
import LgEpgAppsTab from './LgEpgAppsTab';
import { parseIntegrationData } from '../../utils/deviceIntegration';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSocket } from '../../context/SocketContext';
import AppTrainingConfigurator from './AppTrainingConfigurator';
import ModernAppSetup from './ModernAppSetup';
import ExternalSources from './ExternalSources';
import './ExternalSources.css';
import { loadAndroidTvRemoteStatus } from '../Controls/androidTvRemoteApi';
import {
  launchExternalAppProfile,
  listExternalAppProfiles,
  resolveExternalAppAdapter,
  saveExternalAppProfile,
  saveEnabledExternalApps,
  sendExternalAppSetupRemoteAction,
} from './externalAppControlApi';

const isSuitestreamApp = (app) => {
  const packageName = String(app?.package_name || '').toLowerCase();
  const appName = String(app?.app_name || '').toLowerCase();
  return packageName.includes('suitestream') || appName.includes('suitestream');
};

const READINESS_LABELS = {
  needs_setup: 'Needs setup',
  partially_ready: 'Partially ready',
  ready: 'Ready',
  needs_attention: 'Needs attention',
};

const isEnabled = (value) => {
  if (value === false || value === 0) return false;
  return !['0', 'false', 'off', 'no'].includes(String(value ?? '').trim().toLowerCase());
};

const countWorkingActions = (profile) =>
  Object.values(profile?.capabilities?.actions || {})
    .filter((entry) => entry?.calibrated === true).length;

export const resolveAppControlRoute = (remoteStatus, accessibilityEnabled = false) => {
  const targets = Array.isArray(remoteStatus?.targets) ? remoteStatus.targets : [];
  if (targets.some((target) => target.paired)) return 'google_tv';
  return accessibilityEnabled ? 'accessibility' : 'unavailable';
};

function AndroidAppsTab({ device, siteId, externalApp, onManageDeviceApps }) {
  const token = localStorage.getItem('token');
  const socket = useSocket();
  const deviceId = device?.id || null;
  const isAppDevice = Boolean(device?.is_app);

  const [apps, setApps] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [adaptersByPackage, setAdaptersByPackage] = useState({});
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [setupApp, setSetupApp] = useState(null);
  const [controlRoute, setControlRoute] = useState('loading');
  const commandSequenceRef = useRef(0);
  const pendingCommandResponsesRef = useRef(new Map());

  const loadProfiles = useCallback(async () => {
    if (!deviceId) return [];
    const nextProfiles = await listExternalAppProfiles(deviceId);
    setProfiles(nextProfiles);
    return nextProfiles;
  }, [deviceId]);

  const loadApps = useCallback(async () => {
    if (!token || !deviceId) return;
    setStatus('loading');
    setMessage('');
    try {
      const response = await fetch(`/api/devices/get-installed-apps?deviceId=${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to load apps');
      const nextApps = Array.isArray(data.apps) ? data.apps : [];
      setApps(nextApps);
      const externalApps = nextApps.filter((app) => !isSuitestreamApp(app));
      const resolvedAdapters = await Promise.all(
        externalApps.map(async (app) => [
          app.package_name,
          await resolveExternalAppAdapter(app.package_name),
        ])
      );
      setAdaptersByPackage(Object.fromEntries(resolvedAdapters));
      await loadProfiles();
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Failed to load apps');
    }
  }, [deviceId, loadProfiles, token]);

  useEffect(() => {
    setApps([]);
    setProfiles([]);
    setAdaptersByPackage({});
    setSetupApp(null);
    setMessage('');
    setControlRoute('loading');
    if (deviceId && isAppDevice) loadApps();
  }, [deviceId, isAppDevice, loadApps]);

  useEffect(() => {
    if (!deviceId) return undefined;
    let cancelled = false;
    loadAndroidTvRemoteStatus(deviceId)
      .then((result) => {
        if (cancelled) return;
        setControlRoute(resolveAppControlRoute(result, externalApp?.accessibilityEnabled));
      })
      .catch(() => {
        if (!cancelled) {
          setControlRoute(externalApp?.accessibilityEnabled ? 'accessibility' : 'unavailable');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, externalApp?.accessibilityEnabled]);

  const orderedApps = useMemo(() => {
    const list = Array.isArray(apps)
      ? apps.filter((app) => isEnabled(app.enabled) && !isSuitestreamApp(app))
      : [];
    return list.sort((a, b) => {
      const aName = String(a.app_name || a.package_name || '').toLowerCase();
      const bName = String(b.app_name || b.package_name || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [apps]);

  const profilesByPackage = useMemo(
    () => Object.fromEntries(profiles.map((profile) => [profile.packageName, profile])),
    [profiles]
  );

  const sendCommand = useCallback((command, value = null) => {
    if (!socket || !deviceId) return null;
    commandSequenceRef.current += 1;
    const id = `apps-${Date.now()}-${commandSequenceRef.current}`;
    socket.emit('sendCommand', { deviceId, command, value, id });
    return id;
  }, [deviceId, socket]);

  const sendCommandAndWait = useCallback((command, value = null, timeoutMs = 5000) => {
    const requestId = sendCommand(command, value);
    if (!requestId) return Promise.reject(new Error('Device command channel is unavailable'));
    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        pendingCommandResponsesRef.current.delete(requestId);
        resolve(null);
      }, timeoutMs);
      pendingCommandResponsesRef.current.set(requestId, {
        resolve: (payload) => {
          window.clearTimeout(timeoutId);
          resolve(payload);
        },
      });
    });
  }, [sendCommand]);

  useEffect(() => {
    if (!socket || !deviceId) return undefined;
    const handler = (payload) => {
      if (!payload || Number(payload.deviceId) !== Number(deviceId)) return;
      const requestId = payload.requestId || payload.request_id || payload.id || null;
      const pending = requestId ? pendingCommandResponsesRef.current.get(requestId) : null;
      if (pending) {
        pendingCommandResponsesRef.current.delete(requestId);
        pending.resolve(payload);
      }
      if (payload.type === 'list_apps_response') {
        setMessage('Apps refreshed from the TV.');
        window.setTimeout(loadApps, 400);
      } else if (payload.type === 'launch_app_response') {
        setMessage(payload.success ? 'App launched.' : `Launch failed: ${payload.error || 'unknown'}`);
      } else if (payload.type === 'launch_suitestream_response') {
        setMessage(payload.success ? 'Suitestream launched.' : 'Suitestream launch failed.');
      }
    };
    socket.on('deviceCommandResponse', handler);
    return () => {
      socket.off('deviceCommandResponse', handler);
      pendingCommandResponsesRef.current.forEach((pending) => pending.resolve(null));
      pendingCommandResponsesRef.current.clear();
    };
  }, [deviceId, loadApps, socket]);

  const handleLaunch = useCallback(async (app) => {
    if (!app) return;
    if (isSuitestreamApp(app)) {
      sendCommand('launch_suitestream');
      setMessage('Launching Suitestream…');
      return;
    }
    const profile = profiles.find((entry) => entry.packageName === app.package_name);
    if (!profile) {
      sendCommand('launch_app', { package: app.package_name });
      setMessage(`Launching ${app.app_name || app.package_name}…`);
      return;
    }
    setMessage(`Launching ${app.app_name || app.package_name}…`);
    try {
      const result = await launchExternalAppProfile(profile.id);
      const promptRun = result?.execution?.result?.attempts
        ?.flatMap((attempt) => attempt.promptRuns || [])
        .find((run) => run?.status === 'running');
      setMessage(
        result?.automationRun
          ? 'App launched; automatic startup and monitoring are running.'
          : promptRun
          ? 'App launched; watching for trained prompts.'
          : 'App launched.'
      );
    } catch (error) {
      setMessage(
        `Launch failed: ${error.response?.data?.error || error.message || 'unknown error'}`
      );
    }
  }, [profiles, sendCommand]);

  const handleColdLaunch = useCallback(async (packageName) => {
    setMessage('Opening Suitestream before the app…');
    const suitestreamResponse = await sendCommandAndWait('launch_suitestream', null, 5000);
    if (suitestreamResponse?.success === false) {
      throw new Error(suitestreamResponse.error || 'Suitestream launch failed');
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
    setMessage(`Launching ${packageName}…`);
    const appResponse = await sendCommandAndWait(
      'launch_app',
      { package: packageName },
      6000
    );
    if (appResponse?.success === false) {
      throw new Error(appResponse.error || 'App launch failed');
    }
    setMessage(
      appResponse
        ? 'App launch acknowledged; verifying the foreground app…'
        : 'Launch sent; waiting for foreground verification…'
    );
  }, [sendCommandAndWait]);

  const handleTrainingLaunch = useCallback(async (packageName) => {
    const suitestream = packageName === '__suitestream__';
    const response = await sendCommandAndWait(
      suitestream ? 'launch_suitestream' : 'launch_app',
      suitestream ? null : { package: packageName },
      6000
    );
    if (response?.success === false) {
      throw new Error(response.error || `${suitestream ? 'Suitestream' : 'App'} launch failed`);
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1200));
    return response;
  }, [sendCommandAndWait]);

  const handleProfileUpdated = useCallback(async (profile) => {
    if (profile?.packageName) {
      setProfiles((current) => [
        ...current.filter((entry) => entry.packageName !== profile.packageName),
        profile,
      ]);
      return;
    }
    await loadProfiles();
  }, [loadProfiles]);

  const handleReset = useCallback(async (app, profile, adapter) => {
    if (!window.confirm(`Reset the tested setup for ${app.app_name || app.package_name}?`)) return;
    setStatus('loading');
    try {
      const resetProfile = await saveExternalAppProfile(deviceId, app.package_name, {
        displayName: app.app_name || app.package_name,
        adapterKey: adapter?.key || profile?.adapterKey || 'generic',
        adapterVersion: adapter?.version || profile?.adapterVersion || 1,
        readiness: 'needs_setup',
        preferredLaunchMethod: 'app',
        preferredNavigationMethod: 'app',
        controlPolicy: {},
        capabilities: {},
        profile: {},
        lastError: null,
      });
      await handleProfileUpdated(resetProfile);
      setMessage(`${app.app_name || app.package_name} setup was reset.`);
      setSetupApp(app);
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.error || err.message || 'Failed to reset app setup');
    }
  }, [deviceId, handleProfileUpdated]);

  const handleKodiCompatibility = useCallback(async (app, profile) => {
    const label = app.app_name || app.package_name;
    if (!window.confirm(`Treat ${label} as a Kodi-compatible app on this device?`)) return;
    setStatus('loading');
    setMessage('');
    try {
      const adapter = await resolveExternalAppAdapter(app.package_name, 'kodi');
      const savedProfile = await saveExternalAppProfile(deviceId, app.package_name, {
        displayName: label,
        adapterKey: 'kodi',
        adapterVersion: adapter?.version || 1,
        readiness: profile?.readiness || 'needs_setup',
        preferredLaunchMethod: profile?.preferredLaunchMethod || 'app',
        preferredNavigationMethod: profile?.preferredNavigationMethod || 'app',
        controlPolicy: profile?.controlPolicy || {},
        capabilities: profile?.capabilities || {},
        profile: {
          ...(profile?.profile || {}),
          manuallyAssignedAdapter: true,
        },
        lastError: null,
      });
      setAdaptersByPackage((current) => ({
        ...current,
        [app.package_name]: adapter,
      }));
      await handleProfileUpdated(savedProfile);
      setMessage(`${label} will use Kodi-compatible launching and controls.`);
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.error || err.message || 'Failed to assign Kodi compatibility');
    }
  }, [deviceId, handleProfileUpdated]);

  const handleRemoteVisibilityChange = useCallback(async (packageName, enabled) => {
    setStatus('loading');
    setMessage('');
    try {
      const enabledPackages = apps
        .filter((app) =>
          app.package_name === packageName
            ? enabled
            : isSuitestreamApp(app) || isEnabled(app.enabled)
        )
        .map((app) => app.package_name);
      await saveEnabledExternalApps(deviceId, enabledPackages);
      setApps((current) => current.map((app) =>
        app.package_name === packageName ? { ...app, enabled } : app
      ));
      setMessage(enabled ? 'App added to the Remote launcher.' : 'App hidden from the Remote launcher.');
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.message || err.message || 'Failed to update Remote visibility');
    }
  }, [apps, deviceId]);

  if (!deviceId) return <div className="dashboard-app-launcher-empty">Select a device.</div>;
  if (!isAppDevice) {
    return (
      <AssignedAppsPanel key={deviceId} deviceId={deviceId} />
    );
  }

  return (
    <div className="dashboard-app-launcher">
      <AssignedAppsPanel key={deviceId} deviceId={deviceId} />
      <ExternalSources siteId={siteId} deviceId={deviceId} />
      <div className="dashboard-app-launcher-header">
        <div>
          <h3>Apps &amp; shortcuts</h3>
          <p>
            Choose which apps appear on the Remote and configure how each app should launch. Google TV
            Remote is preferred automatically when paired.
          </p>
        </div>
        <div className="dashboard-app-launcher-actions">
          <button type="button" className="secondary" onClick={onManageDeviceApps}>
            Refresh or choose apps
          </button>
        </div>
      </div>

      {status === 'loading' && <div className="dashboard-app-launcher-status">Loading app setup…</div>}
      {message && (
        <div className={`dashboard-app-launcher-status${status === 'error' ? ' error' : ''}`}>
          {message}
        </div>
      )}

      <div className={`apps-control-route apps-control-route--${controlRoute}`}>
        <span>Control route</span>
        <strong>
          {controlRoute === 'google_tv'
            ? 'Google TV Remote · primary'
            : controlRoute === 'accessibility'
              ? 'Accessibility · fallback'
              : controlRoute === 'loading'
                ? 'Checking…'
                : 'No control route available'}
        </strong>
        {controlRoute === 'accessibility' && (
          <small>Legacy control training is shown because no paired Google TV Remote target was found.</small>
        )}
      </div>

      <div className="dashboard-app-launcher-list">
        <div className="dashboard-app-launcher-item readiness-ready">
          <div className="dashboard-app-launcher-row">
            <div className="dashboard-app-launcher-row-main">
              <div className="dashboard-app-launcher-titleline">
                <div className="dashboard-app-launcher-label">Suitestream</div>
                <span className="apps-readiness-badge ready">Built in</span>
              </div>
              <div className="dashboard-app-launcher-meta">
                Return to the Suitestream.tv player and its known starting point.
              </div>
            </div>
            <div className="dashboard-app-launcher-row-actions">
              <button
                type="button"
                className="primary"
                onClick={() => handleLaunch({ app_name: 'Suitestream', package_name: 'suitestream' })}
              >
                Launch Suitestream
              </button>
            </div>
          </div>
        </div>

        {!orderedApps.length && status === 'idle' && (
          <div className="dashboard-app-launcher-empty">
            No external apps are selected. Use “Refresh or choose apps” to add apps from the TV.
          </div>
        )}

        {orderedApps.map((app) => {
          const packageName = app.package_name;
          const label = app.app_name || packageName;
          const suitestream = isSuitestreamApp(app);
          const profile = profilesByPackage[packageName] || null;
          const adapter = adaptersByPackage[packageName] || null;
          const readiness = suitestream || controlRoute === 'google_tv'
            ? 'ready'
            : profile?.readiness || 'needs_setup';
          const expanded = setupApp?.package_name === packageName;
          return (
            <div key={packageName} className={`dashboard-app-launcher-item readiness-${readiness}`}>
              <div className="dashboard-app-launcher-row">
                <div className="dashboard-app-launcher-row-main">
                  <div className="dashboard-app-launcher-titleline">
                    <div className="dashboard-app-launcher-label">{label}</div>
                    <span className={`apps-readiness-badge ${readiness}`}>
                      {suitestream ? 'Built in' : READINESS_LABELS[readiness]}
                    </span>
                    {!suitestream && (
                      <span className={`apps-adapter-badge ${adapter?.matched ? 'known' : 'generic'}`}>
                        {adapter?.matched ? adapter.label : 'Unknown app'}
                      </span>
                    )}
                  </div>
                  <div className="dashboard-app-launcher-pkg">{packageName}</div>
                  {!suitestream && (
                    <div className="dashboard-app-launcher-meta">
                      {controlRoute === 'google_tv'
                        ? 'Google TV Remote controls available'
                        : `${countWorkingActions(profile)} Accessibility controls trained`}
                      {profile?.profile?.launchRecipe?.enabled ? ' · launch automation enabled' : ''}
                      {isEnabled(app.enabled) ? ' · shown on Remote' : ' · hidden from Remote'}
                    </div>
                  )}
                </div>
                <div className="dashboard-app-launcher-row-actions">
                  <button type="button" className="primary" onClick={() => handleLaunch(app)} disabled={!app.launchable}>
                    Launch
                  </button>
                  {!suitestream && (
                    <>
                      <label className="apps-remote-toggle">
                        <input
                          type="checkbox"
                          checked={isEnabled(app.enabled)}
                          onChange={(event) =>
                            handleRemoteVisibilityChange(packageName, event.target.checked)
                          }
                          disabled={status === 'loading'}
                        />
                        Remote
                      </label>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => setSetupApp(
                          expanded && setupApp?._setupMode === 'launch'
                            ? null
                            : { ...app, _setupMode: 'launch' }
                        )}
                      >
                        {expanded && setupApp?._setupMode === 'launch' ? 'Hide Settings' : 'Launch Settings'}
                      </button>
                      {externalApp?.accessibilityEnabled && (
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => setSetupApp(
                            expanded && setupApp?._setupMode === 'fallback'
                              ? null
                              : { ...app, _setupMode: 'fallback' }
                          )}
                        >
                          {expanded && setupApp?._setupMode === 'fallback'
                            ? 'Hide Fallback'
                            : 'Train Accessibility Fallback'}
                        </button>
                      )}
                      {adapter?.key === 'generic' && (
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => handleKodiCompatibility(app, profile)}
                          disabled={status === 'loading'}
                        >
                          Treat as Kodi
                        </button>
                      )}
                      {controlRoute === 'accessibility' && profile && readiness !== 'needs_setup' && (
                        <button
                          type="button"
                          className="secondary danger"
                          onClick={() => handleReset(app, profile, adapter)}
                        >
                          Reset
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {expanded && !suitestream && (
                setupApp?._setupMode !== 'fallback' ? (
                  <ModernAppSetup
                    app={app}
                    profile={profile}
                    deviceId={deviceId}
                    onLaunchApp={() => handleLaunch(app)}
                    onLaunchSuitestream={() => handleLaunch({
                      app_name: 'Suitestream',
                      package_name: 'suitestream',
                    })}
                    onRemoteAction={async (action) => {
                      setMessage(`Sending ${action.replace(/_/g, ' ')}…`);
                      try {
                        const result = await sendExternalAppSetupRemoteAction(deviceId, action);
                        setMessage(
                          `${action.replace(/_/g, ' ')} sent via ${result?.route?.mode || 'Google TV Remote'}.`
                        );
                        return result;
                      } catch (err) {
                        const detail = err.response?.data?.message
                          || err.response?.data?.status
                          || err.message
                          || 'unknown error';
                        setMessage(`Remote command failed: ${detail}`);
                        throw err;
                      }
                    }}
                    controlRoute={controlRoute}
                    accessibilityEnabled={Boolean(externalApp?.accessibilityEnabled)}
                    onClose={() => setSetupApp(null)}
                  />
                ) : externalApp?.accessibilityEnabled ? (
                  <AppTrainingConfigurator
                    app={app}
                    adapter={adapter}
                    deviceId={deviceId}
                    profile={profile}
                    availableApps={orderedApps}
                    onClose={() => setSetupApp(null)}
                    onColdLaunch={handleColdLaunch}
                    onLaunchApp={handleTrainingLaunch}
                    onProfileUpdated={handleProfileUpdated}
                  />
                ) : (
                  <div className="dashboard-app-launcher-empty">
                    Pair Google TV Remote or enable Accessibility before configuring app controls.
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}

function AppsTab(props) {
  const device = props.device;
  const lg = String(device?.type || '').toLowerCase() === 'lgtv' || device?.protocol === 'lg_webos_remote'
    || device?.capabilities?.lgTarget != null
    || parseIntegrationData(device?.integration_data || device?.integrationData).remoteTarget?.protocol === 'lg_webos_remote';
  return lg ? <LgEpgAppsTab key={device.id} device={device} /> : <AndroidAppsTab {...props} />;
}

export default AppsTab;
